import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { IrdProperty } from '@ird/shared-base';
import { noUndefined } from '@ird/shared-utils';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IconPickerOptions, SelectOptions } from '../../models';
import { GenericEditDialogData } from '../../models/generic-edit-dialog-data.model';
import { GenericEditDialogHost } from '../../models/generic-edit-dialog-host.model';
import { GenericAdminService, ValidatorService } from '../../services';
import { selectLabel } from '../generic-table/select-label';
import { ColorPickerInputComponent } from '../color-picker-input';
import { IconPickerComponent } from '../icon-picker';

enum Action {
  SAVE = 'SAVE',
  CANCEL = 'CANCEL',
}

@Component({
  selector: 'shared-generic-edit-dialog',
  templateUrl: './generic-edit-dialog.component.html',
  styleUrls: ['./generic-edit-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ColorPickerInputComponent,
    TranslatePipe,
    IconPickerComponent,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    FlexLayoutModule,
  ],
})
export class GenericEditDialogComponent<Dto> implements OnInit, GenericEditDialogHost<Dto> {
  formGroup: FormGroup;
  data: Dto;
  visibleProperties: IrdProperty<Dto, keyof Dto>[] = [];

  private disabledState$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly _dto: GenericEditDialogData<Dto>,
    private dialogRef: MatDialogRef<GenericEditDialogComponent<Dto>>,
    private formBuilder: FormBuilder,
    private genericAdminService: GenericAdminService<Dto>,
    private validatorService: ValidatorService,
  ) {
    this._dto.actions = [
      {
        text: 'BUTTON_CANCEL',
        color: 'primary',
        type: 'mat-stroked-button',
        action: () => {
          this.close(Action.CANCEL);
          return Subscription.EMPTY;
        },
      },
      {
        text: 'BUTTON_SAVE',
        color: 'primary',
        type: 'mat-raised-button',
        action: () => {
          this.close(Action.SAVE);
          return Subscription.EMPTY;
        },
        disable: () => {
          return this.disabledState$.asObservable();
        },
      },
    ];

    this.data = this._dto.data;
    this.visibleProperties = this.genericAdminService.extractVisibleProperties(this._dto.metadata);

    this.initFormGroup();

    // Subscribe to change event
    this.formGroup.valueChanges.subscribe(() => {
      this.disabledState$.next(this.saveDisabled());
    });
  }

  ngOnInit(): void {
    this.loadFormFieldValues();

    this.saveDisabled();
  }

  getErrorMessage(property: IrdProperty<Dto, keyof Dto>): string {
    const control = this.formGroup.get(property.name.toString());
    if (!control) {
      return '';
    }
    for (const validator of property.validator) {
      if (control.hasError(validator.name)) {
        return validator.errorMsg;
      }
    }
    return '';
  }

  isMultipleSelect(property: IrdProperty<Dto, keyof Dto>): boolean {
    return property.array || Array.isArray(this.data[property.name]);
  }

  getSelectOptions(name: string): unknown[] {
    const fromOptions = (this._dto.options?.[name] as SelectOptions)?.options;
    if (fromOptions) {
      return fromOptions;
    }
    const property = this.visibleProperties.find((p) => String(p.name) === name);
    return property?.allowedValues?.map((v) => v.value) ?? [];
  }

  getSelectLabel(name: string, value: unknown): string {
    const property = this.visibleProperties.find((p) => String(p.name) === name);
    return selectLabel(value, this._dto.options?.[name] as SelectOptions, property?.allowedValues ?? []);
  }

  compareSelectValues = (a: unknown, b: unknown): boolean => {
    if (a === b) {
      return true;
    }
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      return (a as Record<string, unknown>)['id'] === (b as Record<string, unknown>)['id'];
    }
    return false;
  };

  getIconPickerOptions(name: string): IconPickerOptions {
    let url = '';

    if (this._dto.options[name]['url']) {
      url = this._dto.options[name]['url'];
    } else {
      if (this._dto.options[name]['urlFn'] && this._dto.options[name]['urlFn'] instanceof Function) {
        url = this._dto.options[name]['urlFn'](this._dto.dtoId);
      }
    }

    return {
      url: url ?? undefined,
    };
  }

  setIcon(name: string, event?: any): void {
    this.formGroup.get(name).setValue(event ? event.image : undefined);
    this.disabledState$.next(this.saveDisabled());
  }

  initFormGroup(): void {
    if (this._dto.metadata) {
      const group: Record<string, FormControl> = {};

      Object.values(this._dto.metadata.properties).forEach((item: IrdProperty<Dto, keyof Dto>) => {
        // A disabled control drops out of `formGroup.value`, so saving keeps a read-only field's own value.
        group[item.name as string] = new FormControl(
          { value: null, disabled: !item.editable },
          this.validatorService.mapValidator(item.validator),
        );
      });

      this.formGroup = this.formBuilder.group(group);
    }
  }

  private loadFormFieldValues(): void {
    if (this._dto.data) {
      const group: Record<string, unknown> = {};
      Object.values(this._dto.metadata.properties).forEach((item: IrdProperty<Dto, keyof Dto>) => {
        const name = this.genericAdminService.getPropertyName(item);
        if (item.type === 'json') {
          group[name] = this.genericAdminService.formatJson(this._dto.data[name]);
        } else if (item.type === 'Object') {
          group[name] = this.genericAdminService.formatObject(this._dto.data, item, '');
        } else if (item.array) {
          group[name] = this._dto.data[name] ?? [];
        } else {
          group[name] = this._dto.data[name] ?? null;
        }
      });
      this.formGroup.setValue(group);
    }
  }

  private close(action: Action): void {
    if (action === Action.SAVE) {
      const result = { ...this.data, ...this.formGroup.value };
      this.visibleProperties.forEach((property) => {
        const name = property.name as string;
        if (property.type === 'json' && typeof result[name] === 'string') {
          try {
            result[name] = JSON.parse(result[name]);
          } catch {
            // keep as string – the validator should have caught invalid JSON
          }
        } else if (property.type === 'Object') {
          result[name] = this._dto.data[name];
        }
      });
      this.dialogRef.close(noUndefined(result));
    } else {
      this.dialogRef.close();
    }
  }

  private saveDisabled(): boolean {
    const invalidItems: string[] = [];
    Object.keys(this.formGroup.value).forEach((item) => {
      if (this.formGroup.get(item)?.invalid) {
        invalidItems.push(item);
      }
    });
    return invalidItems.length > 0;
  }
}
